import { type RefObject, useEffect } from 'react'

interface TimelineTouchHandlers {
  onPanBy: (dxPx: number, widthPx: number) => void
  onZoomAtCursor: (cursorFraction: number, factor: number) => void
}

/** Horizontal anchor of the gesture: the finger, or the midpoint of two. */
function anchorX(touches: TouchList): number {
  if (touches.length === 1) return touches[0].clientX
  return (touches[0].clientX + touches[1].clientX) / 2
}

/** Distance between the first two fingers (never 0, it is used as a divisor). */
function spread(touches: TouchList): number {
  const dx = touches[0].clientX - touches[1].clientX
  const dy = touches[0].clientY - touches[1].clientY
  return Math.max(1, Math.hypot(dx, dy))
}

/**
 * Touch counterpart of the canvas's mouse gestures: one finger pans the axis,
 * two fingers pinch-zoom around their midpoint. The mouse and wheel handlers
 * stay the desktop path — touch screens never fire them for a drag.
 *
 * The container needs `touch-action: none`, otherwise the browser claims the
 * gesture for scrolling and stops delivering moves.
 */
export function useTimelineTouch(
  containerRef: RefObject<HTMLElement | null>,
  { onPanBy, onZoomAtCursor }: TimelineTouchHandlers
): void {
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    let lastX: number | null = null
    let lastSpread: number | null = null

    // Re-anchor whenever a finger lands or lifts, so the axis doesn't jump
    // when a pinch turns back into a one-finger pan.
    const anchor = (e: TouchEvent) => {
      const touches = e.touches
      lastX = touches.length > 0 ? anchorX(touches) : null
      lastSpread = touches.length > 1 ? spread(touches) : null
    }
    const onMove = (e: TouchEvent) => {
      const touches = e.touches
      if (lastX === null || touches.length === 0) return
      const rect = el.getBoundingClientRect()
      const x = anchorX(touches)
      if (touches.length > 1 && lastSpread !== null) {
        const nextSpread = spread(touches)
        const fraction = Math.max(0, Math.min(1, (x - rect.left) / rect.width))
        onZoomAtCursor(fraction, lastSpread / nextSpread)
        lastSpread = nextSpread
      }
      onPanBy(x - lastX, rect.width)
      lastX = x
    }

    el.addEventListener('touchstart', anchor, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: true })
    el.addEventListener('touchend', anchor, { passive: true })
    el.addEventListener('touchcancel', anchor, { passive: true })
    return () => {
      el.removeEventListener('touchstart', anchor)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', anchor)
      el.removeEventListener('touchcancel', anchor)
    }
  }, [containerRef, onPanBy, onZoomAtCursor])
}
